/* google cannot be detected */
/* eslint no-undef: 0 */
import { getV2UrlByCategoryId } from "~/services/public/Listing"
import { CATEGORY_V4_ID_FURNITURE, CATEGORY_V4_ID_HOUSE_MOVE } from "~/constants/category"
import { V2_FLOORS, V2_PROPERTY_TYPES } from "~/constants/listing"
import { LOCALE, PLACES_LOCALE } from "~/constants/locale"
import { getV4Host } from "~/scripts/useful-functions"
import type VueI18n from "vue-i18n"
import type { AnyVanLocale } from "~/constants/locale"

export async function isAnExcludedIsland(locale: AnyVanLocale, payload: ListingPayload) {
  if (google === undefined) {
    return false
  }

  let excluded = false

  let islands
  switch (locale) {
    case LOCALE.UK: {
      const module = await import("~/constants/islands-uk")
      islands = module.islandsUK
      break
    }
    case LOCALE.ES: {
      const module = await import("~/constants/islands-es")
      islands = module.islandsES
      break
    }
    case LOCALE.FR: {
      const module = await import("~/constants/islands-fr")
      islands = module.islandsFR
      // Temporary restriction - FR IP ready should have Pickup in Paris region
      // Only if v4 flag from unleash enabled, otherwise default to v2 for all France for PPC testing
      if (window?.av?.context?.featureFlags?.fr_prelisting_routing_destination !== 'v4') {
        return true
      }
      if (payload.pickup_address_data?.region !== "Île-de-France") {
        if (
          !module.ipFR.contains(
            new google.maps.LatLng(
              payload.pickup_address_data?.latitude,
              payload.pickup_address_data?.longitude
            )
          )
        ) {
          return true
        }
      }
      break
    }
    case LOCALE.DE: {
      const module = await import("~/constants/islands-de")
      islands = module.islandsDE
      break
    }
    default:
      islands = []
      break
  }

  for (let i = 0; i < islands.length; i++) {
    if (
      (payload.pickup_address_data?.latitude &&
        payload.pickup_address_data?.longitude &&
        islands[i].contains(
          new google.maps.LatLng(
            payload.pickup_address_data?.latitude,
            payload.pickup_address_data?.longitude
          )
        )) ||
      (payload.delivery_address_data?.latitude &&
        payload.delivery_address_data?.longitude &&
        islands[i].contains(
          new google.maps.LatLng(
            payload.delivery_address_data?.latitude,
            payload.delivery_address_data?.longitude
          )
        ))
    ) {
      excluded = true
    }
  }

  return excluded
}

const Iso3166ToLocaleMap: any = {
  /*
   * When adding new territories, add their ISO3166_1 Alpha 2 country code and page locale here.
   * Get page local from nuxt config
   * Get ISO3166_1 Alpha 2 empirically (console.log)
   * No other coding changes should be necessary.
   */
  [PLACES_LOCALE.UK]: LOCALE.UK,
  [PLACES_LOCALE.ES]: LOCALE.ES,
  [PLACES_LOCALE.FR]: LOCALE.FR
  // [PLACES_LOCALE.DE]: LOCALE.DE,
}

export async function shouldUseV2(
  payload: ListingPayload,
  pageLocale: VueI18n.Locale
): Promise<boolean> {
  if (!payload) return true

  if (
    payload.category_id !== CATEGORY_V4_ID_HOUSE_MOVE &&
    payload.category_id !== CATEGORY_V4_ID_FURNITURE
  ) {
    return true
  }

  if (!Object.values(Iso3166ToLocaleMap).includes(pageLocale))
    // locale (based on URL) is not supported
    return true

  if (!payload.pickup_address_data || !payload.delivery_address_data) return true

  if (await isAnExcludedIsland(pageLocale, payload)) return true

  const pickupCountry = payload.pickup_address_data.ISO_3166_1_Alpha_2
  const deliveryCountry = payload.delivery_address_data.ISO_3166_1_Alpha_2

  if (!pickupCountry || !deliveryCountry) return true

  /* Pickup country, delivery country and page country much match to use instant pricing */
  if (
    Iso3166ToLocaleMap[pickupCountry] === Iso3166ToLocaleMap[deliveryCountry] &&
    Iso3166ToLocaleMap[pickupCountry] === pageLocale
  )
    return false

  return true
}

export function redirectToV2(payload: ListingPayload, listing: Listing, locale: string, v4Host: string = "") {
  const form = document.createElement("form")
  const inputFactory = ([name, value]) => {
    const el = document.createElement("input")
    el.setAttribute("type", "hidden")
    el.setAttribute("name", name)
    el.setAttribute("value", value || "")
    form.appendChild(el)
  }

  form.method = "POST"
  form.action =
    process.env.NODE_ENV === "development"
      ? v4Host + "/" + getV2UrlByCategoryId(payload.category_id)
      : "/" + getV2UrlByCategoryId(payload.category_id)

  const attrs: [string, string | null][] = [
    ["from", payload.from || ""],
    ["to", payload.to || ""],
    ["category_id", String(payload.category_id)],
    ["p_latitude", String(payload.pickup_address_data?.latitude)],
    ["p_longitude", String(payload.pickup_address_data?.longitude)],
    ["d_latitude", String(payload.delivery_address_data?.latitude)],
    ["d_longitude", String(payload.delivery_address_data?.longitude)],
    [
      "p_geocode_result",
      JSON.stringify([{ address_components: listing.pickup.address?.place?.address_components }])
    ],
    [
      "d_geocode_result",
      JSON.stringify([{ address_components: listing.delivery.address?.place?.address_components }])
    ],
    ["force_send_goods", "1"]
  ]
  if (payload.category_id === CATEGORY_V4_ID_HOUSE_MOVE) {
    attrs.push(
      ["origin_type_of_property", V2_PROPERTY_TYPES[String(payload.origin_type_of_property)]],
      [
        "destination_type_of_property",
        V2_PROPERTY_TYPES[String(payload.destination_type_of_property)]
      ],
      ["origin_property_level", V2_FLOORS[String(payload.origin_property_level)]],
      ["destination_property_level", V2_FLOORS[String(payload.destination_property_level)]]
    )
  }

  window.av?.track("cms-frontend", "redirect_v2", {
    attrs,
    locale
  })

  attrs.forEach(inputFactory)
  document.body.appendChild(form)
  form.submit()
}

export function redirectToError(host: string, locale: string) {
  window.location.href = getV4Host(host, locale) + "/error"
}
